/**
 * Every user-visible name in one place.
 *
 * The product name is a wholly invented word built from the sentence that
 * describes the game. That is the strongest kind of trademark — fanciful, not
 * descriptive of anyone else's product — and it is why nothing in this file
 * needs a disclaimer. See docs/06-legal.md.
 */

const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five'] as const;

export const BRAND = {
  /**
   * Shown in the title bar, lobby and share sheet. Said "pull-let-ee-wow".
   *
   * Written as a word, not as an acronym in capitals. It *is* an acronym, but
   * it is pronounced as a word, and a wall of capitals reads as shouting
   * everywhere it appears. The capitals live in `expansion`, where they are
   * doing a job: showing which letters the name was taken from.
   */
  name: 'Puletiwo',

  /** The sentence the name is built from. */
  expansion: 'PUt LEtters Together Into WOrds VErtically and HOrizontally',

  /** The unabridged name, kept for the lobby's small print. */
  longName: 'Puletiwoveho',

  /** The same, in capitals, for comparing against `expansion`. */
  initials: 'PULETIWOVEHO',

  pronunciation: 'pull-let-ee-wow',

  tagline: 'Put letters together into words, vertically and horizontally — ' +
           'and shout when you run out',

  /**
   * What a player calls when their rack empties.
   *
   * The number is the match's draw setting, so a table playing one tile a
   * round shouts "TAKE 1" and a table playing three shouts "TAKE 3". The call
   * names what actually happens rather than a fixed rule.
   */
  callPhrase(drawPerRound: number): string {
    return `TAKE ${drawPerRound}`;
  },

  /** The same call written out, for prose and screen readers. */
  callPhraseWords(drawPerRound: number): string {
    const word = NUMBER_WORDS[drawPerRound] ?? String(drawPerRound);
    return `take ${word}`;
  },

  describe(): string { return `${this.name} — ${this.tagline}`; },
} as const;
